import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Badge, BadgeList, TagBadge } from "../../components/badge";
import { BigTextPreview } from "../../components/big-text-preview";
import { Button, ButtonRow, DangerButton } from "../../components/button";
import { CodeEditor } from "../../components/code-editor";
import { ColorPickerControl } from "../../components/color-picker";
import { CommandBar } from "../../components/command-bar";
import { DateTimeLabel } from "../../components/datetime-label";
import { Dropdown, DropdownDivider, DropdownItem } from "../../components/dropdown";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DataPickerControl, MultiDataPickerControl } from "../../components/data-picker";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { FormControl, NumberInput, TextInput } from "../../components/form";
import { GatewayPickerControl } from "../../components/gateway-picker";
import { Icon } from "../../components/icon";
import { Image, ImageContainer, ImageControl } from "../../components/image";
import { Section } from "../../components/layout";
import { Link } from "../../components/link";
import { LogRowItem } from "../../components/log-row-item";
import { SuggestionButtonEditor } from "../../components/suggestion-button-editor";
import { TabControl } from "../../components/tabcontrol";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../components/table";
import { CheckWrapper, CopiableText, DashboardTextHeading, Heading, MutedText, Paragraph, TextHeading, TextViewer } from "../../components/text";
import { TimeSpanPicker } from "../../components/time-span-picker";
import { TtsVoiceControl, TtsVoiceHint } from "../../components/tts-voice-control";
import { Constants } from "../../constants";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { copyToClipboard } from "../../ui/clipboard";
import { closeFlyout, showFlyout } from "../../ui/flyout";
import { formatDuration, formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { throttledCall } from "../../ui/throttle";
import { getUniqueColor } from "../../ui/unique-color";
import { ellipsis, find, valueFrom } from "../../util";
import { Card, CardContent, CardGrid, CardHeader } from "../../components/card";
import { IntegrationCard, IntegrationGrid } from "../../components/integration-card";
import { showNewChatSessionDialog } from "./session-dialog";

const ScheduleGridOptions = {
    style: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "6px 15px"
    }
};

export default function (ctx) {

    const chatClientId = ctx.params.id;
    const isCreatingChatClient = chatClientId == "create";

    const chatClient = el.state.create(null);
    const chatSessions = el.state.create(null);
    const sessionFilter = el.state.create("");
    const scheduledMessages = el.state.create(null);
    const scheduledMessageUserFilter = el.state.create("");

    function updateView() {
        runOperation("Fetching chat client...", async () => {
            const response = await api.getJson(`/api/v1/web-chat-client/${chatClientId}`);
            chatClient.value = response.data;
        });
    }

    if (!isCreatingChatClient) {
        updateView();
        refreshSessions();
        refreshScheduledMessages();
    }

    scheduledMessageUserFilter.subscribe(() => {
        if (!isCreatingChatClient) {
            refreshScheduledMessages();
        }
    });

    async function refreshSessions() {
        const response = await api.getJson(`/api/v1/web-chat-client/${chatClientId}/sessions?` + new URLSearchParams({
            filter: sessionFilter.value
        }).toString());
        chatSessions.value = response.data;
    }

    async function refreshScheduledMessages() {
        const response = await api.getJson(`/api/v1/web-chat-clients/${chatClientId}/hook-schedules?external-user-id=${encodeURIComponent(scheduledMessageUserFilter.value)}`);
        scheduledMessages.value = response.data;
    }

    async function cancelSchedule(scheduleId, flyoutTarget) {
        await runOperation("Cancelling schedule...", async () => {
            await api.deleteJson(`/api/v1/web-chat-clients/${chatClientId}/hook-schedules/schedules/${scheduleId}`);
            await refreshScheduledMessages();
            closeFlyout(flyoutTarget);
        });
    }

    async function cancelRecurringTask(taskId, flyoutTarget) {
        await runOperation("Cancelling recurring task...", async () => {
            await api.deleteJson(`/api/v1/web-chat-clients/${chatClientId}/hook-schedules/recurring/${taskId}`);
            await refreshScheduledMessages();
            closeFlyout(flyoutTarget);
        });
    }

    async function openScheduleDetails(scheduleId) {
        await runOperation("Loading schedule details...", async () => {
            const response = await api.getJson(`/api/v1/web-chat-clients/${chatClientId}/hook-schedules/schedules/${scheduleId}`);
            const schedule = response.data;

            displayAlert({
                title: "Schedule details",
                dialogSize: "medium",
                message: el.fragment(
                    Card(
                        CardHeader("Schedule information"),
                        CardContent(
                            ScheduleGridOptions,
                            LogRowItem("ID", CopiableText(schedule.id)),
                            LogRowItem("External user ID", schedule.externalUserId && CopiableText(schedule.externalUserId) || MutedText("-")),
                            LogRowItem("Target", schedule.target || MutedText("-")),
                            LogRowItem("Target identifier", schedule.targetIdentifier && CopiableText(schedule.targetIdentifier) || MutedText("-"))
                        )
                    ),
                    Card(
                        CardHeader("Timing"),
                        CardContent(
                            ScheduleGridOptions,
                            LogRowItem("Scheduled time", schedule.scheduledTime && DateTimeLabel(schedule.scheduledTime) || MutedText("-")),
                            LogRowItem("Created at", schedule.createdAt && DateTimeLabel(schedule.createdAt) || MutedText("-"))
                        )
                    ),
                    schedule.reason && Card(
                        CardHeader("Reason"),
                        CardContent(
                            ScheduleGridOptions,
                            LogRowItem("Reason", TextViewer(schedule.reason)),
                            LogRowItem("Context", TextViewer(schedule.context))
                        )
                    ),
                    schedule.context && Card(
                        CardHeader("Result"),
                        CardContent(
                            ScheduleGridOptions,
                            LogRowItem("Activated at", schedule.activatedAt && DateTimeLabel(schedule.activatedAt) || MutedText("Not activated")),
                            LogRowItem("Activation result", schedule.activationResult && TextViewer(schedule.activationResult) || MutedText("-"))
                        )
                    )
                ),
                buttons: [{ label: "Close" }]
            });
        });
    }

    async function openRecurringTaskDetails(taskId) {
        await runOperation("Loading task details...", async () => {
            const response = await api.getJson(`/api/v1/web-chat-clients/${chatClientId}/hook-schedules/recurring/${taskId}`);
            const task = response.data;

            displayAlert({
                title: "Recurring task details",
                dialogSize: "medium",
                message: el.fragment(
                    Card(
                        CardHeader("Task information"),
                        CardContent(
                            ScheduleGridOptions,
                            LogRowItem("ID", CopiableText(task.id)),
                            LogRowItem("External user ID", task.externalUserId && CopiableText(task.externalUserId) || MutedText("-")),
                            LogRowItem("Target", task.target || MutedText("-")),
                            LogRowItem("Target identifier", task.targetIdentifier && CopiableText(task.targetIdentifier) || MutedText("-")),
                            LogRowItem("Cron expression", task.cronExpression && CopiableText(task.cronExpression) || MutedText("-")),
                            task.jobDescription && LogRowItem("Description", task.jobDescription)
                        )
                    ),
                    Card(
                        CardHeader("Execution"),
                        CardContent(
                            ScheduleGridOptions,
                            LogRowItem("Execution count", String(task.executionCount ?? 0)),
                            task.maxExecutions && LogRowItem("Max executions", String(task.maxExecutions)),
                            LogRowItem("Last activation", task.lastActivation && DateTimeLabel(task.lastActivation) || MutedText("Never")),
                            LogRowItem("Next activation", task.nextActivation && DateTimeLabel(task.nextActivation) || MutedText("-")),
                            LogRowItem("Created at", task.createdAt && DateTimeLabel(task.createdAt) || MutedText("-"))
                        )
                    )
                ),
                buttons: [{ label: "Close" }]
            });
        });
    }

    function confirmCancelSchedule(scheduleId) {
        showFlyout(
            Paragraph("Are you sure you want to cancel this schedule? This action cannot be undone."),
            DangerButton("Yes, cancel schedule", {
                onClick(event) {
                    cancelSchedule(scheduleId, event.target);
                }
            })
        );
    }

    function confirmCancelRecurringTask(taskId) {
        showFlyout(
            Paragraph("Are you sure you want to cancel this recurring task? This action cannot be undone."),
            DangerButton("Yes, cancel task", {
                onClick(event) {
                    cancelRecurringTask(taskId, event.target);
                }
            })
        );
    }

    function handleSave() {
        const data = {
            name: valueFrom("chatClientName"),
            aiGatewayId: valueFrom("chatClientAssociatedGateway"),
            clientParameters: {
                languageCode: valueFrom("chatClientLanguage"),
                primaryColor: valueFrom("chatClientPrimaryColor"),
                pageTitle: valueFrom("chatClientPageTitle"),
                helloLabel: valueFrom("chatClientHelloLabel")?.nullWhenEmpty(),
                helloSubLabel: valueFrom("chatClientHelloSubLabel")?.nullWhenEmpty(),
                logoImageUrl: valueFrom("chatClientLogoImageSource")?.nullWhenEmpty(),
                suggestionButtons: valueFrom("chatClientSuggestionButtons"),
                textAreaPlaceholder: valueFrom("chatClientPlaceholderLabel")?.nullWhenEmpty(),
                customStyles: valueFrom("chatClientCustomCss")?.nullWhenEmpty(),
                customScripts: valueFrom("chatClientCustomJavaScript")?.nullWhenEmpty(),
                inputModes: valueFrom("chatClientInputModes"),
                debug: valueFrom("chatClientEnableDebug"),
                showToolCalls: valueFrom("chatClientShowToolCalls"),
                audioSynthesisSource: valueFrom("chatClientAudioSynthesisSource"),
                audioSynthesisVoice: valueFrom("chatClientAudioSynthesisVoice"),
                audioSynthesisInstruction: valueFrom("chatClientAudioSynthesisInstruction"),
                summarizeTextBeforeAudioSynthesis: valueFrom("chatClientAudioSynthesisSummarize"),
                splitAnswerIntoMessageChunks: valueFrom("chatClientSplitAnswerIntoMessageChunks"),
                allowScheduledContinuations: valueFrom("chatClientAllowScheduledContinuations"),
                maxScheduledIgnoredZone: valueFrom("chatClientMaxScheduledIgnoredZone"),
                messageDebounceInterval: valueFrom("chatClientMessageDebounceInterval"),
                uploadUnsupportedFiles: valueFrom("chatClientUploadUnsupportedFiles")
            },
            limitingParameters: {
                messagesPerHour: valueFrom("chatClientMessagesPerHour") ?? 0,
                maxMessages: valueFrom("chatClientMaxMessages")
            }
        };

        runOperation("Saving chat client...", async () => {
            if (isCreatingChatClient) {
                const response = await api.postJson("/api/v1/web-chat-client", data);
                page(`/dashboard/chat-clients/${response.data.id}`);
            } else {
                await api.putJson(`/api/v1/web-chat-client/${chatClientId}`, data);
            }
        });
    }

    return el.state.listening([chatClient], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(isCreatingChatClient ? "New chat client" : DashboardTextHeading(chatClient.value?.name, "Chat client details")),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        isCreatingChatClient ? "Create chat client" : "Save changes",
                        {
                            onClick: handleSave
                        }
                    ),
                    !isCreatingChatClient && Dropdown("Manage",
                        DropdownItem("Conversation logs", Icon("dropdown-list"), {
                            onClick() {
                                page(`/dashboard/conversations?${new URLSearchParams({
                                    queryFilter: `--chat-client ${chatClientId}`
                                }).toString()}`);
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Delete", Icon("delete-bin-7-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Delete chat client",
                                    message: "Are you sure you want to delete this chat client and their sessions? This action cannot be undone.",
                                    type: "alert-warning",
                                    buttons: [
                                        {
                                            label: "Delete chat client",
                                            onClick(event) {
                                                runOperation("Deleting chat client...", async () => {
                                                    await api.deleteJson(`/api/v1/web-chat-client/${chatClientId}`);
                                                    page("/dashboard/chat-clients");
                                                });
                                                event.target.closest("dialog").dismiss();
                                            }
                                        },
                                        {
                                            label: "Cancel",
                                            onClick(event) {
                                                event.target.closest("dialog").dismiss();
                                            }
                                        }
                                    ]
                                });
                            }
                        })
                    )
                )
            ),
            Section(
                TabControl([
                    {
                        title: "Chat client",
                        icon: Icon("question-answer-line"),
                        contents: el.fragment(
                            Heading("Client setup",
                                Paragraph("Set the public name, gateway and interface language for this chat client.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientName",
                                        value: chatClient.value?.name,
                                        renderValue(value) {
                                            return Button(value || "No name");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Chat client name", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.length > 3, "The chat client name must be at least 3 characters long."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Chat client name"),
                                        Paragraph("Specifies a friendly name for your chat client.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientAssociatedGateway",
                                        value: chatClient.value?.aiGateway?.id,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: GatewayPickerControl({ value, allowNone: false }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Associated gateway"),
                                        Paragraph("Specify which gateway this chat client will send messages through.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientLanguage",
                                        value: chatClient.value?.clientParameters.languageCode || null,
                                        renderValue(value) {
                                            return Button(value || "Auto detect");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    value,
                                                    data: [
                                                        {
                                                            value: null,
                                                            text: "Auto detect"
                                                        },
                                                        {
                                                            value: "pt-BR",
                                                            text: "Português Brasileiro"
                                                        },
                                                        {
                                                            value: "en-US",
                                                            text: "English (US)"
                                                        }
                                                    ]
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("UI language"),
                                        Paragraph("Specify the language that the chat client will use in interface components, such as buttons.")
                                    )
                                ),
                            )
                        )
                    },
                    {
                        title: "Customization",
                        icon: Icon("quill-pen-line"),
                        contents: el.fragment(
                            Heading("Branding",
                                Paragraph("Control the page-level identity used by the chat client.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientPrimaryColor",
                                        value: chatClient.value?.clientParameters.primaryColor || getUniqueColor(Math.random().toString()),
                                        renderValue(value) {
                                            return ColorPickerControl(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: ColorPickerControl(value, { classList: ["large"] }),
                                                onConfirm: callback,

                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Primary color"),
                                        Paragraph("Choose the highlight color for links, buttons, and other elements.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientPageTitle",
                                        value: chatClient.value?.clientParameters.pageTitle || "My web chat",
                                        renderValue(value) {
                                            return Button(value || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Page title", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: [
                                                    [x => x.length > 3, "The page title must be at least 3 characters long."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Page title"),
                                        Paragraph("Define the title that will be displayed on the page tab.")
                                    )
                                )
                            ),
                            Heading("Welcome state",
                                Paragraph("Customize the empty conversation screen shown before the first message.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientHelloLabel",
                                        value: chatClient.value?.clientParameters.helloLabel,
                                        renderValue(value) {
                                            return Button(ellipsis(value) || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Presentation header", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Presentation title"),
                                        Paragraph("Choose the presentation title of the chat for the first time you access it."),
                                        Paragraph("This title is displayed in the center of the page when there is no message.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientHelloSubLabel",
                                        value: chatClient.value?.clientParameters.helloSubLabel,
                                        renderValue(value) {
                                            return Button(ellipsis(value) || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Presentation sub-label", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Presentation subtitle"),
                                        Paragraph("This text is displayed below the presentation title.")
                                    )
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientLogoImageSource",
                                        value: chatClient.value?.clientParameters.logoImageUrl,
                                        renderValue(value) {
                                            return ImageContainer(value, 72)
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: ImageControl({ value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Logo image"),
                                        Paragraph("Specify a logo image to display at the opening of the chat.")
                                    )
                                ),
                            ),
                            Heading("Conversation starters",
                                Paragraph("Configure prompts and input copy that help users begin a conversation.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientSuggestionButtons",
                                        value: chatClient.value?.clientParameters.suggestionButtons ?? [],
                                        renderValue(value) {
                                            return Button(value?.length > 0 ? value.length + " buttons" : MutedText("Empty"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: SuggestionButtonEditor({ value }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Suggestion buttons"),
                                        Paragraph("Specify suggestion buttons that will be displayed above the user input field to quickly start a new conversation."),
                                        Paragraph("These buttons are conversation starters for the user to quickly start a new conversation")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientPlaceholderLabel",
                                        value: chatClient.value?.clientParameters.textAreaPlaceholder,
                                        renderValue(value) {
                                            return Button(ellipsis(value) || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Talk with the assistant", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Input area placeholder"),
                                        Paragraph("Specify the placeholder that is displayed in the user input field.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Parameters",
                        icon: Icon("list-settings-line"),
                        contents: el.fragment(
                            Heading("Session limits",
                                Paragraph("Define message limits and accepted input modes for each chat session.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientMessagesPerHour",
                                        value: chatClient.value?.limitingParameters.messagesPerHour,
                                        renderValue(value) {
                                            return Button(value && formatNumber(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 5000, step: 5 }),
                                                onConfirm: value => callback(value ?? 0),
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum messages per hour"),
                                        Paragraph("Controls how many messages a session can send per hour."),
                                        Paragraph("Unset disables the hourly limit.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientMaxMessages",
                                        value: chatClient.value?.limitingParameters.maxMessages,
                                        renderValue(value) {
                                            return Button(value && formatNumber(value) || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 50000, step: 5 }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum messages"),
                                        Paragraph("Controls how many messages a session can hold."),
                                        Paragraph("This parameter includes both user and assistant messages.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientInputModes",
                                        value: chatClient.value?.clientParameters.inputModes ?? [],
                                        renderValue(value) {
                                            return Button(value.join(", ").nullWhenEmpty() || MutedText("Only raw text"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: MultiDataPickerControl({
                                                    value,
                                                    data: [
                                                        { value: "Image", text: "Image" },
                                                        { value: "Audio", text: "Audio" },
                                                        { value: "Document", text: "Document" },
                                                    ]
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Input modes"),
                                        Paragraph("Specifies which input modes are allowed in the chat client."),
                                        Paragraph("Note that certain modes must be compatible with the model used by the gateway.")
                                    )
                                ),
                            )
                        )
                    },
                    {
                        title: "Text-to-speech",
                        icon: Icon("volume-up-line"),
                        contents: el.fragment(
                            Heading("Voice generation",
                                Paragraph("Choose the speech provider, voice and synthesis instructions.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisSource",
                                        value: chatClient.value?.clientParameters.audioSynthesisSource?.at(0),
                                        renderValue(value) {
                                            return Button(Constants.TtsModels[value]?.name || MutedText("None"));
                                        },
                                        valueChanged(modelName) {
                                            const model = Constants.TtsModels[modelName];
                                            const voiceEditor = find("chatClientAudioSynthesisVoice");
                                            voiceEditor.value = model?.defaultVoice ?? null;

                                            find("chatClientAudioSynthesisVoiceHint")?.replaceWith(
                                                TtsVoiceHint(modelName, { id: "chatClientAudioSynthesisVoiceHint" })
                                            );
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    value,
                                                    data: [
                                                        {
                                                            value: "None",
                                                            text: TextHeading(null, "None", "Cost: -")
                                                        },
                                                        ...Object.entries(Constants.TtsModels).map(([modelName, model]) => ({
                                                            value: modelName,
                                                            text: TextHeading(
                                                                null,
                                                                model.name,
                                                                BadgeList(TagBadge(model.price))
                                                            )
                                                        }))
                                                    ],
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Voice Synthesis Origin"),
                                        Paragraph("Defines the origin from where text-to-speech is generated in the conversation.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisVoice",
                                        value: chatClient.value?.clientParameters.audioSynthesisVoice,
                                        renderValue(value) {
                                            return Button(value || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            const modelName = valueFrom("chatClientAudioSynthesisSource");
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TtsVoiceControl(modelName, value, {
                                                    ariaLabel: "Synthesis voice"
                                                }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Synthesis Voice"),
                                        Paragraph("Defines which voice will produce the audio."),
                                        Paragraph(
                                            TtsVoiceHint(
                                                chatClient.value?.clientParameters.audioSynthesisSource?.at(0),
                                                { id: "chatClientAudioSynthesisVoiceHint" }
                                            )
                                        )
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisInstruction",
                                        value: chatClient.value?.clientParameters.audioSynthesisInstruction,
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Special Instructions"),
                                        Paragraph("Defines tones, speech patterns, and speaking styles.")
                                    )
                                ),
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisSummarize",
                                        value: chatClient.value?.clientParameters.summarizeTextBeforeAudioSynthesis,
                                        renderValue(value) {
                                            return CheckWrapper(value);
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Summarize before synthesis"),
                                        Paragraph("Shortens and optimizes the text to be synthesized before it is synthesized. This can help reduce costs and improve audio quality.")
                                    )
                                ),
                            )
                        )
                    },
                    !isCreatingChatClient && {
                        title: "Sessions",
                        icon: Icon("discuss-line"),
                        contents: el.fragment(
                            Section(
                                CommandBar(
                                    Button(Icon("add-line"), "New session", {
                                        onClick() {
                                            showNewChatSessionDialog(chatClientId, refreshSessions);
                                        }
                                    }),
                                    Button(Icon("refresh-line"), "Refresh", {
                                        onClick() {
                                            refreshSessions();
                                        }
                                    })
                                )
                            ),
                            Section(
                                CommandBar(
                                    FormControl(null,
                                        TextInput("chatClientSessionFilter", "Find sessions...", {
                                            onInput(event) {
                                                throttledCall(async () => {
                                                    sessionFilter.value = event.target.value;
                                                    await refreshSessions();
                                                }, 500);
                                            }
                                        })
                                    )
                                )
                            ),
                            Section(
                                Card(
                                    CardHeader("Chat sessions"),
                                    CardContent(
                                        Table(
                                            {
                                                pagination: {
                                                    perPage: 80
                                                }
                                            },
                                            TableHeader(
                                                TableHeaderCell("Session key"),
                                                TableHeaderCell("Tag/User"),
                                                TableHeaderCell("Usage"),
                                                TableHeaderCell("Created at"),
                                                TableHeaderCell("Last activity"),
                                                TableHeaderCell("Context"),
                                                TableHeaderCell("Actions")
                                            ),
                                            el.state.listening([chatSessions], () =>
                                                TableBody(
                                                    chatSessions.value?.map(session =>
                                                        TableRow(
                                                            TableCell(
                                                                CopiableText(ellipsis(session.sessionKey, 15), session.sessionKey)
                                                            ),
                                                            TableCell(
                                                                session.tagName && CopiableText(ellipsis(session.tagName, 15), session.tagName) || MutedText("None")
                                                            ),
                                                            TableCell(
                                                                el("div",
                                                                    formatNumber(session.tokenCount), " tokens"
                                                                ),
                                                                el("small",
                                                                    MutedText(
                                                                        formatNumber(session.messageCount), " messages"
                                                                    )
                                                                )
                                                            ),
                                                            TableCell(
                                                                DateTimeLabel(session.createdAt)
                                                            ),
                                                            TableCell(
                                                                session.lastMessage && DateTimeLabel(session.lastMessage) || MutedText("Never")
                                                            ),
                                                            TableCell(
                                                                ellipsis(session.contextPreview, 40)?.nullWhenEmpty() || MutedText("None")
                                                            ),
                                                            TableActionCell(
                                                                ButtonRow(
                                                                    Button(Icon("chat-forward-line"), {
                                                                        onClick() {
                                                                            window.open(`/chat/${session.sessionKey}`, "_blank");
                                                                        }
                                                                    }),
                                                                    Dropdown(null,
                                                                        DropdownItem("Copy link", Icon("file-copy-line"), {
                                                                            onClick() {
                                                                                copyToClipboard(`${window.location.origin}/chat/${session.sessionKey}`)
                                                                            }
                                                                        }),
                                                                        DropdownDivider(),
                                                                        DropdownItem("Export conversation", Icon("upload-2-line"), {
                                                                            onClick() {
                                                                                window.open(window.env.apiEndpoint + `/api/v1/public/chat-clients/${session.sessionKey}/transcript.json`, "_blank");
                                                                            }
                                                                        }),
                                                                        DropdownItem("View conversation logs", Icon("dropdown-list"), {
                                                                            onClick() {
                                                                                page(`/dashboard/conversations?${new URLSearchParams({
                                                                                    queryFilter: `--chat-session ${session.id}`
                                                                                }).toString()}`);
                                                                            }
                                                                        }),
                                                                        DropdownDivider(),
                                                                        DropdownItem("Clear conversation", Icon("delete-back-line"), {
                                                                            onClick() {
                                                                                showFlyout(
                                                                                    Paragraph("This will clear the user conversation, but not the session itself. Do you want to continue?"),
                                                                                    DangerButton("Yes, clear the chat session", {
                                                                                        onClick() {
                                                                                            runOperation("Clearing session...", async () => {
                                                                                                await api.postJson(`/api/v1/public/chat-clients/${session.sessionKey}/reset-state`);
                                                                                                await refreshSessions();
                                                                                            });
                                                                                        }
                                                                                    })
                                                                                )
                                                                            }
                                                                        }),
                                                                        DropdownItem("Delete", Icon("delete-bin-7-line"), {
                                                                            onClick() {
                                                                                showFlyout(
                                                                                    Paragraph("This will delete the entire session conversation. Do you want to continue?"),
                                                                                    DangerButton("Yes, delete the session", {
                                                                                        onClick() {
                                                                                            runOperation("Deleting session...", async () => {
                                                                                                await api.deleteJson(`/api/v1/web-chat-client/${chatClientId}/sessions/${session.id}`);
                                                                                                await refreshSessions();
                                                                                            });
                                                                                        }
                                                                                    })
                                                                                )
                                                                            }
                                                                        })
                                                                    )
                                                                )
                                                            )
                                                        )
                                                    )?.nullWhenEmpty() || DefaultRow("No chat sessions found."),
                                                    {
                                                        mount() {
                                                            this.closest("table").refreshPagination();
                                                        }
                                                    }
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    },
                    !isCreatingChatClient && {
                        title: "Scheduled messages",
                        icon: Icon("calendar-schedule-line"),
                        contents: el.fragment(
                            Section(
                                CommandBar(
                                    FormControl("Filter by user",
                                        TextInput("scheduledMessageUserFilter", "Filter by user", {
                                            onInput(event) {
                                                throttledCall(() => scheduledMessageUserFilter.value = event.target.value, 300);
                                            }
                                        })
                                    ),
                                    FormControl(null,
                                        Button(Icon("refresh-line"), "Refresh", {
                                            onClick: refreshScheduledMessages
                                        })
                                    )
                                )
                            ),
                            el.state.listening([scheduledMessages], () => el.fragment(
                                Section(
                                    Card(
                                        CardHeader("Scheduled messages", "One-time messages scheduled for future delivery."),
                                        CardContent(
                                            Table(
                                                { class: "hoverable", pagination: { perPage: 25 } },
                                                TableHeader(
                                                    TableHeaderCell("External user ID"),
                                                    TableHeaderCell("Target"),
                                                    TableHeaderCell("Target identifier"),
                                                    TableHeaderCell("Scheduled time"),
                                                    TableHeaderCell("Status"),
                                                    TableHeaderActionCell("Actions")
                                                ),
                                                TableBody(
                                                    scheduledMessages.value?.schedules?.map(schedule =>
                                                        TableRow(
                                                            TableCell(
                                                                schedule.externalUserId && CopiableText(schedule.externalUserId) || MutedText("-")
                                                            ),
                                                            TableCell(schedule.target || MutedText("-")),
                                                            TableCell(
                                                                schedule.targetIdentifier && CopiableText(schedule.targetIdentifier) || MutedText("-")
                                                            ),
                                                            TableCell(
                                                                schedule.scheduledTime && DateTimeLabel(schedule.scheduledTime) || MutedText("-")
                                                            ),
                                                            TableCell(
                                                                Badge(schedule.state)
                                                            ),
                                                            TableActionCell(
                                                                ButtonRow(
                                                                    Button(Icon("eye-line"), {
                                                                        onClick() {
                                                                            openScheduleDetails(schedule.id);
                                                                        }
                                                                    }),
                                                                    Button(Icon("delete-bin-7-line"), {
                                                                        onClick() {
                                                                            confirmCancelSchedule(schedule.id);
                                                                        }
                                                                    })
                                                                )
                                                            )
                                                        )
                                                    )?.nullWhenEmpty() || DefaultRow("No scheduled messages found."),
                                                    {
                                                        mount() {
                                                            this.closest("table").refreshPagination();
                                                        }
                                                    }
                                                )
                                            )
                                        )
                                    )
                                ),
                                Section(
                                    Card(
                                        CardHeader("Recurring tasks", "Cron-based tasks that activate repeatedly."),
                                        CardContent(
                                            Table(
                                                { class: "hoverable", pagination: { perPage: 25 } },
                                                TableHeader(
                                                    TableHeaderCell("External user ID"),
                                                    TableHeaderCell("Target"),
                                                    TableHeaderCell("Target identifier"),
                                                    TableHeaderCell("Cron expression"),
                                                    TableHeaderCell("Next activation"),
                                                    TableHeaderCell("Executions"),
                                                    TableHeaderActionCell("Actions")
                                                ),
                                                TableBody(
                                                    scheduledMessages.value?.recurringTasks?.map(task =>
                                                        TableRow(
                                                            TableCell(
                                                                task.externalUserId && CopiableText(task.externalUserId) || MutedText("-")
                                                            ),
                                                            TableCell(task.target || MutedText("-")),
                                                            TableCell(
                                                                task.targetIdentifier && CopiableText(task.targetIdentifier) || MutedText("-")
                                                            ),
                                                            TableCell(
                                                                task.cronExpression && CopiableText(task.cronExpression) || MutedText("-")
                                                            ),
                                                            TableCell(
                                                                task.nextActivation && DateTimeLabel(task.nextActivation) || MutedText("-")
                                                            ),
                                                            TableCell(
                                                                String(task.executionCount ?? 0),
                                                                task.maxExecutions && el("small", MutedText(` / ${task.maxExecutions}`))
                                                            ),
                                                            TableActionCell(
                                                                ButtonRow(
                                                                    Button(Icon("eye-line"), {
                                                                        onClick() {
                                                                            openRecurringTaskDetails(task.id);
                                                                        }
                                                                    }),
                                                                    Button(Icon("delete-bin-7-line"), {
                                                                        onClick() {
                                                                            confirmCancelRecurringTask(task.id);
                                                                        }
                                                                    })
                                                                )
                                                            )
                                                        )
                                                    )?.nullWhenEmpty() || DefaultRow("No recurring tasks found."),
                                                    {
                                                        mount() {
                                                            this.closest("table").refreshPagination();
                                                        }
                                                    }
                                                )
                                            )
                                        )
                                    )
                                )
                            ))
                        )
                    },
                    !isCreatingChatClient && {
                        title: "Integrations",
                        icon: Icon("box-3-line"),
                        contents: el.fragment(
                            Section(
                                IntegrationGrid(
                                    IntegrationCard({
                                        name: "Telegram",
                                        imageUrl: "/assets/Telegram_2019_Logo.svg.png",
                                        href: `/dashboard/chat-clients/${chatClientId}/integrations/telegram`,
                                        tags: [
                                            chatClient.value?.integrations.telegramIntegration?.botToken && "Connected"
                                        ],
                                        onDelete: chatClient.value?.integrations.telegramIntegration?.botToken && function () {
                                            displayAlert({
                                                title: "Delete Telegram integration",
                                                message: "Are you sure you want to remove the Telegram integration?",
                                                type: "alert-warning",
                                                buttons: [
                                                    {
                                                        label: "Delete integration",
                                                        onClick(event) {
                                                            runOperation("Deleting integration...", async () => {
                                                                await api.deleteJson(`/api/v1/web-chat-client/${chatClientId}/integrations/Telegram`);
                                                                updateView();
                                                            });
                                                            event.target.closest("dialog").dismiss();
                                                        }
                                                    },
                                                    {
                                                        label: "Cancel",
                                                        onClick(event) { event.target.closest("dialog").dismiss(); }
                                                    }
                                                ]
                                            });
                                        }
                                    }),
                                    IntegrationCard({
                                        name: "Evolution API",
                                        imageUrl: "/assets/evolution-api-logo.png",
                                        href: `/dashboard/chat-clients/${chatClientId}/integrations/evolution-api`,
                                        tags: [
                                            chatClient.value?.integrations.evolutionIntegration?.apiKey && "Connected"
                                        ],
                                        onDelete: chatClient.value?.integrations.evolutionIntegration?.apiKey && function () {
                                            displayAlert({
                                                title: "Delete Evolution API integration",
                                                message: "Are you sure you want to remove the Evolution API integration?",
                                                type: "alert-warning",
                                                buttons: [
                                                    {
                                                        label: "Delete integration",
                                                        onClick(event) {
                                                            runOperation("Deleting integration...", async () => {
                                                                await api.deleteJson(`/api/v1/web-chat-client/${chatClientId}/integrations/EvolutionApi`);
                                                                updateView();
                                                            });
                                                            event.target.closest("dialog").dismiss();
                                                        }
                                                    },
                                                    {
                                                        label: "Cancel",
                                                        onClick(event) { event.target.closest("dialog").dismiss(); }
                                                    }
                                                ]
                                            });
                                        }
                                    }),
                                    IntegrationCard({
                                        name: "Kapso",
                                        imageUrl: "/assets/kapso-logo.png",
                                        href: `/dashboard/chat-clients/${chatClientId}/integrations/kapso`,
                                        classList: "dark-logo-parameter",
                                        tags: [
                                            chatClient.value?.integrations.kapsoIntegration?.apiKey && "Connected"
                                        ],
                                        onDelete: chatClient.value?.integrations.kapsoIntegration?.apiKey && function () {
                                            displayAlert({
                                                title: "Delete Kapso integration",
                                                message: "Are you sure you want to remove the Kapso integration?",
                                                type: "alert-warning",
                                                buttons: [
                                                    {
                                                        label: "Delete integration",
                                                        onClick(event) {
                                                            runOperation("Deleting integration...", async () => {
                                                                await api.deleteJson(`/api/v1/web-chat-client/${chatClientId}/integrations/Kapso`);
                                                                updateView();
                                                            });
                                                            event.target.closest("dialog").dismiss();
                                                        }
                                                    },
                                                    {
                                                        label: "Cancel",
                                                        onClick(event) { event.target.closest("dialog").dismiss(); }
                                                    }
                                                ]
                                            });
                                        }
                                    }),
                                    IntegrationCard({
                                        name: "Z-Api",
                                        imageUrl: "/assets/z-api-logo.webp",
                                        href: `/dashboard/chat-clients/${chatClientId}/integrations/z-api`,
                                        classList: ["dark-logo-parameter", "legacy"],
                                        tags: [
                                            chatClient.value?.integrations.zapiIntegration?.instanceId && "Connected",
                                            "Legacy"
                                        ],
                                        onDelete: chatClient.value?.integrations.zapiIntegration?.instanceId && function () {
                                            displayAlert({
                                                title: "Delete Z-Api integration",
                                                message: "Are you sure you want to remove the Z-Api integration?",
                                                type: "alert-warning",
                                                buttons: [
                                                    {
                                                        label: "Delete integration",
                                                        onClick(event) {
                                                            runOperation("Deleting integration...", async () => {
                                                                await api.deleteJson(`/api/v1/web-chat-client/${chatClientId}/integrations/Zapi`);
                                                                updateView();
                                                            });
                                                            event.target.closest("dialog").dismiss();
                                                        }
                                                    },
                                                    {
                                                        label: "Cancel",
                                                        onClick(event) { event.target.closest("dialog").dismiss(); }
                                                    }
                                                ]
                                            });
                                        }
                                    }),
                                )
                            ),
                            Heading("Messaging",
                                Paragraph("Configure messaging behavior shared across all integrations.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientSplitAnswerIntoMessageChunks",
                                        value: chatClient.value?.clientParameters.splitAnswerIntoMessageChunks ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "Enabled", "Disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Split answer into message chunks"),
                                        Paragraph("Specify whether to split the assistant's answers into multiple messages.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientMessageDebounceInterval",
                                        value: chatClient.value?.clientParameters.messageDebounceInterval ?? 5,
                                        renderValue(value) {
                                            return Button(formatDuration(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TimeSpanPicker({ value, timeUnits: ["m", "s"] }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x >= 1, "The interval must be at least 1 second."],
                                                    [x => x <= 300, "The interval must be at most 5 minutes."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Message debounce interval"),
                                        Paragraph("Specifies how long to wait before processing a message after user input stops.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientUploadUnsupportedFiles",
                                        value: chatClient.value?.clientParameters.uploadUnsupportedFiles ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value);
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Upload unsupported files"),
                                        Paragraph("Allow sending files that are not natively supported by the model.")
                                    )
                                )
                            ),
                            Heading("Scheduled messages",
                                Paragraph("Define the behavior for scheduled and continued messages.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientAllowScheduledContinuations",
                                        value: chatClient.value?.clientParameters.allowScheduledContinuations ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "Allow", "Disallow");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Allow continued conversations"),
                                        Paragraph("Specify whether to allow the assistant to schedule messages and actions for continued conversations.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientMaxScheduledIgnoredZone",
                                        value: chatClient.value?.clientParameters.maxScheduledIgnoredZone ?? 259200,
                                        renderValue(value) {
                                            return Button(formatDuration(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TimeSpanPicker({ value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x >= 86400, "The duration must be greater than or equal to 1 day."],
                                                    [x => x <= 7776000, "The duration must be less than or equal to 90 days."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum ignored time for continued messages"),
                                        Paragraph("Specify the maximum duration that a continued message can be ignored before being discarded.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Advanced",
                        icon: Icon("terminal-box-line"),
                        contents: el.fragment(
                            Heading("Custom code",
                                Paragraph("Inject custom CSS and JavaScript into the chat client.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientCustomCss",
                                        value: chatClient.value?.clientParameters.customStyles,
                                        renderValue(value) {
                                            return BigTextPreview(value, "css")
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value, language: "css" }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Custom styles"),
                                        Paragraph("Specify custom styles (CSS) to enable in your chat client.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientCustomJavaScript",
                                        value: chatClient.value?.clientParameters.customScripts,
                                        renderValue(value) {
                                            return BigTextPreview(value, "javascript")
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value, language: "javascript" }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Custom script"),
                                        Paragraph("Specify custom scripts (JavaScript) to modify the behavior of your chat client.")
                                    )
                                )
                            ),
                            Heading("Diagnostics",
                                Paragraph("Expose additional runtime details while testing and debugging.")
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientEnableDebug",
                                        value: chatClient.value?.clientParameters.debug ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Debug"),
                                        Paragraph("Enabling debug mode causes advanced messages to be displayed in the browser console.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientShowToolCalls",
                                        value: chatClient.value?.clientParameters.showToolCalls ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "visible", "hidden");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Tool call visibility"),
                                        Paragraph("Activates or deactivates if function calls should be displayed during the conversation."),
                                        Paragraph("Function calls with errors will only display error details if Debug is active.")
                                    )
                                )
                            )
                        )
                    }
                ].filter(Boolean), {
                    type: "fixed"
                })
            )
        )
    )
}
